import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { completeNewPassword as completeNewPasswordChallenge } from '../auth/cognitoAuth';
import { getPendingChallenge, clearPendingChallenge } from '../auth/pendingChallenge';
import Footer from '../components/Footer';

/**
 * Its own route (/set-new-password), not an inline state swap on /login -
 * reached only via navigate() from Login.jsx's newPasswordRequired branch.
 *
 * The live CognitoUser challenge object comes from pendingChallenge.js (a
 * plain module-scoped singleton), NOT React Router's location.state -
 * CognitoUser holds an internal Storage reference that history.pushState()
 * can't structured-clone, so passing it via navigate(path, { state })
 * throws at navigation time. See pendingChallenge.js for the full reasoning.
 *
 * Same practical constraint as before either way: this only survives
 * client-side navigation within the same page load. A hard reload, a
 * bookmark, or pasting this URL directly always lands here with nothing in
 * the singleton, which is treated as "no challenge to complete" and
 * bounces back to /login rather than rendering a broken form.
 */
export default function SetNewPassword() {
  const navigate = useNavigate();
  const challenge = getPendingChallenge();

  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  if (!challenge) {
    return <Navigate to="/login" replace />;
  }

  const { user, userAttributes, from } = challenge;

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await completeNewPasswordChallenge(user, newPassword, userAttributes);
      clearPendingChallenge();
      navigate(from || '/', { replace: true });
    } catch (err) {
      setError(err.message || 'Could not set new password.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-page d-flex flex-column min-vh-100">
      <div className="flex-grow-1 d-flex align-items-center justify-content-center">
        <div className="card auth-card shadow-sm border-0">
          <div className="auth-accent-bar" />
          <div className="card-body">
            <div className="text-center mb-4">
              <img src="/bizmo-logo.png" alt="Bizmoto" height="44" />
            </div>
            <form onSubmit={handleSubmit}>
              <h1 className="h5 mb-2 text-center">Set a new password</h1>
              <p className="text-muted small text-center mb-3">
                Your account was just provisioned by an admin - choose a permanent password to
                continue.
              </p>
              <div className="mb-3">
                <label className="form-label small">New password</label>
                <input
                  type="password"
                  className="form-control"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={12}
                  autoComplete="new-password"
                />
              </div>
              {error && <p className="text-danger small">{error}</p>}
              <button type="submit" className="btn btn-primary w-100" disabled={submitting}>
                {submitting ? 'Saving…' : 'Set password and continue'}
              </button>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
